import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ALL_TRADES = ['HVAC', 'Plumbing', 'Electrical'];

const C = {
  text: '#0E1A2B', sub: '#3D4E63', muted: '#6B7A8D',
  border: '#E3E8EE', borderStrong: '#D2DAE3', surface: '#F7F9FB',
  lime: '#C6F450', limeHover: '#B4E13C', limeTint: '#F3FBDD', limeDeep: '#55760A',
};

const fmt = (n) => `$${Number(n).toLocaleString()}`;

const PRICE_STATES = {
  monthly: {
    1: { name: '1 Trade', pill: null, note: 'Full access for your selected trade.' },
    2: { name: '2-Trade Bundle', pill: 'SAVE 10%', note: 'Bundle pricing applied automatically.' },
    3: { name: '3-Trade Bundle', pill: 'SAVE 20%', note: 'Bundle pricing applied automatically.' },
  },
  annual: {
    1: { name: '1 Trade', pill: '2 MONTHS FREE', note: 'Full access for your selected trade.' },
    2: { name: '2-Trade Bundle', pill: '2 MONTHS FREE', note: 'Bundle savings plus two months free.' },
    3: { name: '3-Trade Bundle', pill: 'SAVE 33%', note: 'Save 33% compared with purchasing 3 individual trades monthly for 12 months.' },
  },
};

const StepLabel = ({ num, children }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <span className="flex h-6 w-6 items-center justify-center rounded-full text-[12px] font-bold text-white" style={{ backgroundColor: C.text }} aria-hidden="true">{num}</span>
    <span className="text-[12px] font-bold uppercase tracking-[0.08em]" style={{ color: C.sub }}>{children}</span>
  </div>
);

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const planParam = searchParams.get('plan') || 'monthly';

  const [billing, setBillingState] = useState(planParam === 'annual' ? 'annual' : 'monthly');
  const [selectedTrades, setSelectedTrades] = useState(() => {
    const pre = searchParams.get('trades');
    return pre ? pre.split(',').filter((t) => ALL_TRADES.includes(t)) : [];
  });
  const [isFounder, setIsFounder] = useState(planParam === 'lifetime' || planParam === 'founding');
  const [tiersData, setTiersData] = useState(null);
  const [agreedLegal, setAgreedLegal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [paymentState, setPaymentState] = useState(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMsg, setPromoMsg] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/pricing/tiers`).then((r) => r.json()).then(setTiersData).catch(() => setTiersData({ tiers: {}, founders: { remaining: 0, total: 25 }, test_mode: false }));
    if (sessionId) {
      setPaymentState('checking');
      pollPaymentStatus(sessionId, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollPaymentStatus = async (sid, attempts) => {
    if (attempts >= 8) { setPaymentState('error'); return; }
    try {
      const res = await fetch(`${API}/api/checkout/status/${sid}`);
      const data = await res.json();
      if (data.payment_status === 'paid') { setPaymentState('success'); return; }
      if (data.status === 'expired') { setPaymentState('expired'); return; }
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch { setPaymentState('error'); }
  };

  const count = isFounder ? 3 : selectedTrades.length;
  const tierKey = isFounder
    ? 'founding_lifetime_all_trades'
    : count > 0 ? `${billing}_${count}_trade${count > 1 ? '_bundle' : ''}` : null;
  const tier = tierKey && tiersData ? tiersData.tiers[tierKey] : null;
  const founders = tiersData?.founders || { remaining: 0, total: 25 };
  const foundingTier = tiersData?.tiers?.founding_lifetime_all_trades;
  const annual3 = tiersData?.tiers?.annual_3_trade_bundle;
  const foundingOneTime = foundingTier && !foundingTier.interval;
  const unit = billing === 'monthly' ? '/month' : '/year';
  const state = !isFounder && count > 0 ? PRICE_STATES[billing][count] : null;

  const setBilling = (b) => { if (isFounder) setIsFounder(false); setBillingState(b); };
  const toggleTrade = (id) => {
    if (isFounder) {
      setIsFounder(false);
      setSelectedTrades(ALL_TRADES.filter((t) => t !== id));
      return;
    }
    setSelectedTrades((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
  };
  const chooseFounder = () => {
    if (founders.remaining <= 0) return;
    setIsFounder(true);
    setSelectedTrades(ALL_TRADES);
    setBillingState('annual');
  };

  const recordLegal = () =>
    fetch(`${API}/api/legal/agree`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ documents: ['terms', 'privacy', 'subscription'] }),
    });

  const applyPromo = async () => {
    if (!agreedLegal) { setPromoMsg({ ok: false, text: 'Please agree to the terms first, then apply your code.' }); return; }
    setPromoLoading(true); setPromoMsg(null);
    try {
      await recordLegal();
      const res = await fetch(`${API}/api/billing/redeem-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ code: promoCode, trades: null }),
      });
      const data = await res.json();
      if (!res.ok) setPromoMsg({ ok: false, text: data.detail || 'This promo code is invalid, expired, or unavailable.' });
      else {
        setPromoMsg({ ok: true, text: 'Promo code applied — full access unlocked, no card needed. Taking you in…' });
        setTimeout(() => { window.location.href = '/start-here'; }, 1500);
      }
    } catch { setPromoMsg({ ok: false, text: 'Something went wrong. Try again.' }); }
    finally { setPromoLoading(false); }
  };

  const handleCheckout = async () => {
    if (loading || !agreedLegal || (!isFounder && selectedTrades.length === 0)) return;
    setLoading(true); setCheckoutError(null);
    try {
      await recordLegal();
      const res = await fetch(`${API}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          plan: isFounder ? 'lifetime' : billing,
          trades: isFounder ? ALL_TRADES : selectedTrades,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not start checkout');
      if (data.url) { window.location.href = data.url; return; }
      throw new Error('No checkout URL received');
    } catch (e) {
      setCheckoutError(e.message || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (paymentState) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-xl border p-10 text-center" style={{ borderColor: C.borderStrong }} data-testid="payment-status-panel">
          {paymentState === 'checking' && (
            <>
              <div className="animate-spin w-12 h-12 border-4 border-t-transparent rounded-full mx-auto mb-6" style={{ borderColor: `${C.lime} transparent ${C.lime} ${C.lime}` }} />
              <h1 className="text-2xl font-bold mb-2" style={{ color: C.text }} data-testid="payment-checking-message">Verifying Your Payment...</h1>
              <p style={{ color: C.sub }}>Please wait, this only takes a few seconds.</p>
            </>
          )}
          {paymentState === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: C.lime }}>
                <Check style={{ color: C.text }} size={36} />
              </div>
              <h1 className="text-3xl font-bold mb-3" style={{ color: C.text }} data-testid="payment-success-message">Payment Successful!</h1>
              <p className="mb-8" style={{ color: C.sub }}>Your trades are unlocked. Welcome to the Vault.</p>
              <button onClick={() => navigate('/dashboard')} data-testid="payment-success-dashboard-btn"
                className="font-bold py-3.5 px-8 rounded-[10px] text-lg transition-colors"
                style={{ backgroundColor: C.lime, color: C.text }}>
                Go to Your Dashboard →
              </button>
            </>
          )}
          {(paymentState === 'error' || paymentState === 'expired') && (
            <>
              <h1 className="text-2xl font-bold mb-3" style={{ color: C.text }} data-testid="payment-error-message">
                {paymentState === 'expired' ? 'Payment Session Expired' : 'Payment Verification Timed Out'}
              </h1>
              <p className="mb-8" style={{ color: C.sub }}>
                {paymentState === 'expired'
                  ? 'Your checkout session expired. Please try again.'
                  : 'We could not confirm your payment yet. If you were charged, access will unlock automatically — or contact support.'}
              </p>
              <button onClick={() => navigate(`/checkout?plan=${planParam}`)} data-testid="payment-retry-btn"
                className="font-bold py-3 px-8 rounded-[10px]" style={{ backgroundColor: C.lime, color: C.text }}>
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!tiersData) {
    return <div className="min-h-screen bg-white flex items-center justify-center" style={{ color: C.muted }}>Loading pricing…</div>;
  }

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" style={{ color: C.text }}>
      {/* Logo bar */}
      <header className="border-b" style={{ borderColor: C.border }}>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-3 flex items-center">
          <button onClick={() => navigate('/')} data-testid="checkout-logo-link" className="rounded-lg px-2.5 py-1.5" style={{ backgroundColor: C.text }}>
            <img src="/gritclicks-logo.png" alt="GritClicks" className="h-8 w-auto" />
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold" data-testid="checkout-headline">Choose Your GritClicks Plan</h1>
          <p className="mt-2 text-base" style={{ color: C.sub }} data-testid="checkout-subheadline">Select your trades and billing option. You can change your plan later.</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_352px]">
          <main>
            {/* STEP 1 — BILLING */}
            <section className="mb-8" data-testid="billing-toggle">
              <StepLabel num="1">Billing</StepLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['monthly', 'annual'].map((b) => {
                  const active = billing === b && !isFounder;
                  return (
                    <button key={b} onClick={() => setBilling(b)} aria-pressed={active} data-testid={`billing-toggle-${b}`}
                      className="relative flex items-center gap-2.5 rounded-[10px] border-2 px-4 py-3.5 text-left transition-colors min-h-[52px]"
                      style={{ borderColor: active ? C.text : C.border, backgroundColor: active ? C.surface : '#fff' }}>
                      <span aria-hidden="true" className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2"
                        style={{ borderColor: active ? C.text : C.borderStrong }}>
                        {active && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: C.text }} />}
                      </span>
                      <span className="font-bold capitalize">{b}</span>
                      {b === 'annual' && (
                        <span className="ml-auto rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-bold whitespace-nowrap" style={{ backgroundColor: C.lime, color: C.text }}>2 MONTHS FREE</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* STEP 2 — TRADES */}
            <section className="mb-5">
              <StepLabel num="2">Choose Your Trades</StepLabel>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="group" aria-label="Trade selection">
                {ALL_TRADES.map((id) => {
                  const selected = isFounder || selectedTrades.includes(id);
                  return (
                    <button key={id} onClick={() => toggleTrade(id)} aria-pressed={selected} data-testid={`trade-card-${id.toLowerCase()}`}
                      className="flex items-center gap-3 rounded-[10px] border-2 px-4 py-3.5 text-left transition-colors min-h-[52px]"
                      style={{ borderColor: selected ? C.text : C.border, backgroundColor: selected ? C.surface : '#fff' }}>
                      <span aria-hidden="true" className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2"
                        style={{ borderColor: selected ? C.text : C.borderStrong, backgroundColor: selected ? C.text : '#fff' }}>
                        {selected && <Check size={14} strokeWidth={3.5} style={{ color: C.lime }} />}
                      </span>
                      <span className="font-bold">{id}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* DYNAMIC PRICE CARD */}
            <section className="mb-6">
              <div className="rounded-[11px] p-5" style={{ backgroundColor: C.surface, border: `1.5px solid ${C.border}` }} data-testid="bundle-summary" aria-live="polite">
                {!isFounder && !tier && (
                  <p className="text-sm font-semibold" style={{ color: C.muted }} data-testid="price-empty-state">Select a trade to see your price</p>
                )}
                {!isFounder && tier && state && (
                  <>
                    <p className="text-sm font-semibold" style={{ color: C.sub }} data-testid="bundle-plan-label">{state.name}</p>
                    <div className="mt-1 flex flex-wrap items-baseline gap-3">
                      <p className="font-extrabold whitespace-nowrap" style={{ fontSize: 38, lineHeight: 1.1 }} data-testid="bundle-total">
                        {fmt(tier.amount)}<span className="text-base font-semibold" style={{ color: C.sub }}>{unit}</span>
                      </p>
                      {state.pill && (
                        <span className="rounded-full px-2.5 py-1 text-[11px] font-bold whitespace-nowrap" style={{ backgroundColor: C.lime, color: C.text }} data-testid="bundle-badge">{state.pill}</span>
                      )}
                    </div>
                    <p className="mt-1.5 text-sm" style={{ color: C.muted }} data-testid="bundle-note">{state.note}</p>
                  </>
                )}
                {isFounder && foundingTier && (
                  <>
                    <p className="text-sm font-semibold" style={{ color: C.sub }} data-testid="bundle-plan-label">{foundingTier.label}</p>
                    <p className="mt-1 font-extrabold" style={{ fontSize: 38, lineHeight: 1.1 }} data-testid="bundle-total">
                      {fmt(foundingTier.amount)}<span className="text-base font-semibold" style={{ color: C.sub }}> {foundingOneTime ? 'one time' : '/year'}</span>
                    </p>
                    <p className="mt-1.5 text-sm" style={{ color: C.muted }}>All three trades included.</p>
                  </>
                )}
              </div>
            </section>

            {/* FOUNDING CARD — amounts rendered from backend values */}
            {foundingTier && (
              <section>
                <div className="rounded-[11px] p-5" style={{ backgroundColor: C.limeTint, border: `1.5px solid ${C.limeHover}` }} data-testid="founding-card">
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em]" style={{ color: C.limeDeep }}>Founding {founders.total}</p>
                  <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-extrabold">All Three Trades</h3>
                      <p className="text-sm" style={{ color: C.sub }}>HVAC + Plumbing + Electrical</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-extrabold" data-testid="founding-price">
                        {fmt(foundingTier.amount)} <span className="text-sm font-semibold" style={{ color: C.sub }}>{foundingOneTime ? 'one time' : 'first year'}</span>
                      </p>
                    </div>
                  </div>
                  {annual3 && (
                    <p className="mt-2 text-[13px]" style={{ color: C.sub }} data-testid="founding-compare">
                      Standard 3-Trade Annual: {fmt(annual3.amount)} · Limited to the first {founders.total} contractors.
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    {isFounder ? (
                      <button onClick={() => setIsFounder(false)} data-testid="founding-choose-btn" aria-pressed="true"
                        className="rounded-[10px] px-5 py-2.5 font-bold text-sm border-2 min-h-[44px]"
                        style={{ borderColor: C.text, color: C.text, backgroundColor: '#fff' }}>
                        ✓ Selected
                      </button>
                    ) : (
                      <button onClick={chooseFounder} disabled={founders.remaining <= 0} data-testid="founding-choose-btn" aria-pressed="false"
                        className="rounded-[10px] px-5 py-2.5 font-bold text-sm text-white min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ backgroundColor: C.text }}>
                        Choose Founding {founders.total}
                      </button>
                    )}
                    {founders.remaining <= 0 && <span className="text-xs font-semibold" style={{ color: C.muted }}>All spots claimed</span>}
                  </div>
                  <p className="mt-2.5 text-[12px]" style={{ color: C.muted }} data-testid="founding-disclosure">
                    {foundingOneTime
                      ? 'One-time payment. Does not renew.'
                      : 'Renews after year one at the then-current standard 3-trade annual rate.'}
                  </p>
                </div>
              </section>
            )}
          </main>

          {/* ORDER SUMMARY */}
          <aside className="lg:sticky lg:top-8 lg:self-start">
            <div className="rounded-[11px] bg-white p-5" style={{ border: `1px solid ${C.borderStrong}` }} aria-live="polite">
              <h2 className="text-base font-extrabold mb-4">Your Plan</h2>
              {(isFounder || count > 0) ? (
                <div className="space-y-2 text-sm" data-testid="order-summary">
                  <div className="flex justify-between gap-3">
                    <span style={{ color: C.muted }}>Plan</span>
                    <span className="font-bold text-right" data-testid="order-plan">
                      {isFounder ? `Founding ${founders.total}` : state?.name}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span style={{ color: C.muted }}>Trades</span>
                    <span className="text-right" data-testid="order-trades">{(isFounder ? ALL_TRADES : selectedTrades).join(' + ')}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span style={{ color: C.muted }}>Billing</span>
                    <span data-testid="order-billing">
                      {isFounder ? (foundingOneTime ? 'One-time payment' : 'Annual') : billing === 'monthly' ? 'Monthly' : 'Annual'}
                    </span>
                  </div>
                  <div className="my-3 border-t" style={{ borderColor: C.border }} />
                  <div className="flex justify-between gap-3">
                    <span style={{ color: C.muted }}>Plan price</span>
                    <span className="font-semibold" data-testid="order-price">
                      {tier ? `${fmt(tier.amount)}${isFounder ? (foundingOneTime ? '' : '/yr') : unit}` : '—'}
                    </span>
                  </div>
                  {!isFounder && tier && tier.savings > 0 && (
                    <div className="flex justify-between gap-3">
                      <span style={{ color: C.muted }}>Bundle savings</span>
                      <span className="font-bold" style={{ color: C.limeDeep }} data-testid="order-savings">–{fmt(tier.savings)}{unit}</span>
                    </div>
                  )}
                  <div className="my-3 border-t" style={{ borderColor: C.border }} />
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-extrabold">Due today</span>
                    <span className="text-2xl font-extrabold" data-testid="order-total">{tier ? fmt(tier.amount) : '—'}</span>
                  </div>
                  <p className="text-[12px]" style={{ color: C.muted }} data-testid="renewal-disclosure">
                    {isFounder
                      ? (foundingOneTime ? 'One-time payment. Does not renew.' : 'Renews after year one at the standard 3-trade annual rate.')
                      : billing === 'annual'
                        ? `Renews yearly at ${fmt(tier?.amount || 0)}. Cancel anytime.`
                        : `Renews monthly at ${fmt(tier?.amount || 0)}. Cancel anytime.`}
                  </p>
                </div>
              ) : (
                <p className="text-sm" style={{ color: C.muted }} data-testid="order-empty">Select a trade to see your plan.</p>
              )}

              {/* Terms */}
              <label className="mt-5 flex items-start gap-2.5 cursor-pointer" data-testid="legal-agree-label">
                <input type="checkbox" checked={agreedLegal} onChange={(e) => setAgreedLegal(e.target.checked)}
                  className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-[#0E1A2B]" data-testid="legal-agree-checkbox" />
                <span className="text-[13px]" style={{ color: C.sub }}>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold underline" style={{ color: C.text }} data-testid="legal-terms-link">Terms</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold underline" style={{ color: C.text }} data-testid="legal-privacy-link">Privacy Policy</a>.
                </span>
              </label>

              {checkoutError && (
                <p role="alert" className="mt-3 text-sm font-semibold text-red-600" data-testid="checkout-error">{checkoutError}</p>
              )}
              <button onClick={handleCheckout} aria-busy={loading}
                disabled={loading || !agreedLegal || (!isFounder && selectedTrades.length === 0)}
                data-testid="checkout-pay-btn"
                className="mt-4 w-full rounded-[10px] py-3.5 font-extrabold text-[15px] min-h-[48px] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: C.lime, color: C.text }}
                onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = C.limeHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = C.lime; }}>
                {loading ? 'Preparing secure checkout…' : 'Continue to Secure Checkout'}
              </button>
              <p className="mt-2.5 text-center text-[12px]" style={{ color: C.muted }}>
                Secure payment powered by Stripe{tiersData.test_mode ? ' · Test Mode' : ''}
              </p>

              {/* Promo */}
              <div className="mt-3 text-center" data-testid="promo-code-section">
                {!promoOpen ? (
                  <button onClick={() => setPromoOpen(true)} data-testid="promo-toggle-link"
                    className="text-[13px] underline underline-offset-2" style={{ color: C.muted }}>
                    Add promo code
                  </button>
                ) : (
                  <div className="text-left">
                    <div className="flex gap-2">
                      <input value={promoCode} onChange={(e) => { setPromoCode(e.target.value); setPromoMsg(null); }}
                        placeholder="Enter promo code" data-testid="promo-code-input" aria-label="Promo code"
                        className="min-w-0 flex-1 rounded-[9px] px-3 py-2 text-sm uppercase placeholder:normal-case focus:outline-none"
                        style={{ border: `1px solid ${C.borderStrong}`, color: C.text }} />
                      <button onClick={applyPromo} disabled={promoLoading || !promoCode.trim()} data-testid="promo-apply-btn"
                        className="rounded-[9px] px-4 py-2 text-sm font-bold disabled:opacity-40"
                        style={{ backgroundColor: C.text, color: '#fff' }}>
                        {promoLoading ? '…' : 'Apply'}
                      </button>
                    </div>
                    {promoMsg && (
                      <p role="status" className="mt-2 text-[13px] font-semibold" style={{ color: promoMsg.ok ? C.limeDeep : '#C2410C' }} data-testid="promo-message">{promoMsg.text}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
